import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    linkedSignal,
    signal,
    viewChildren,
} from '@angular/core';
import {FormControl, NonNullableFormBuilder, ReactiveFormsModule} from '@angular/forms';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router, RouterLink, RouterOutlet} from '@angular/router';
import {catchError, combineLatest, EMPTY, map, of, startWith, switchMap} from 'rxjs';
import {DateTime} from 'luxon';
import {
    DatePipe,
    InputDateGroupComponent,
    InputDropdownCheckboxesComponent,
    InputTextGroupComponent,
    LoadComponent,
    PaginationComponent,
    SelectableItem,
    applySortingToHeaders,
    SortableTableHeaderComponent,
    SortDirection,
    Sorting,
} from '@brunata-angular/ui';
import {
    caseInsensitiveComparator,
    downloadCsv,
    formatStreetAddress,
    fromControl,
    includesIgnoreCase,
    keyComparator,
    numericLocaleComparator,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {InstallationProjectsService} from './installation-projects.service';
import {
    INSTALLATION_PROJECT_STATUSES,
    InstallationProject,
    InstallationProjectStatus,
    installationProjectStatusDisplay,
    InstallationProjectStatusDisplay,
} from './installation-projects.model';

type InstallationProjectsView =
    | {status: 'loading'}
    | {status: 'error'}
    | {status: 'success'; projects: InstallationProject[]};

type InstallationProjectRow = {
    project: InstallationProject;
    address: string;
    statusDisplay: InstallationProjectStatusDisplay;
    awaitingCustomerInfo: boolean;
};

/** All statuses a project can carry, in the order the status filter lists them. */
const FILTERABLE_STATUSES: readonly InstallationProjectStatus[] = [
    ...INSTALLATION_PROJECT_STATUSES,
    'unknown',
];

@Component({
    selector: 'app-installation-projects',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        TranslatePipe,
        LoadComponent,
        DatePipe,
        SortableTableHeaderComponent,
        InputTextGroupComponent,
        InputDateGroupComponent,
        InputDropdownCheckboxesComponent,
        PaginationComponent,
        RouterLink,
        RouterOutlet,
    ],
    templateUrl: './installation-projects.component.html',
    styleUrl: './installation-projects.component.scss',
})
export class InstallationProjectsComponent {
    private readonly headers = viewChildren(SortableTableHeaderComponent);

    private readonly installationProjectsService = inject(InstallationProjectsService);
    private readonly translateService = inject(TranslateService);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);

    /** The list is also the `:projectId` detail's parent shell (the select-building-page shape),
     * so it renders itself only while no child route is active. Recomputed on every router event
     * rather than just NavigationEnd, because the rxjs `filter` operator would shadow the filter
     * field below. */
    protected readonly noActivatedChildren = toSignal(
        this.router.events.pipe(
            startWith(null),
            map(() => this.activatedRoute.children.length === 0),
        ),
        {requireSync: true},
    );

    /** Column order of the table; `column` doubles as sort key and filter control name. Every
     * column sorts, so only the filter widget varies. */
    protected readonly columns = [
        {
            column: 'projectNumber',
            labelKey: 'installationProjects_column_projectId',
            filter: 'text',
        },
        {
            column: 'buildingNumber',
            labelKey: 'installationProjects_column_buildingId',
            filter: 'text',
        },
        {
            column: 'buildingName',
            labelKey: 'installationProjects_column_project',
            filter: 'text',
        },
        {
            column: 'status',
            labelKey: 'installationProjects_column_status',
            filter: 'status',
        },
        {
            column: 'address',
            labelKey: 'installationProjects_column_address',
            filter: 'text',
        },
        {
            column: 'customerInformation',
            labelKey: 'installationProjects_column_customerInformation',
            filter: 'customerInfo',
        },
        {
            column: 'plannedStart',
            labelKey: 'installationProjects_column_plannedStart',
            filter: 'date',
        },
        {
            column: 'expectedCompletion',
            labelKey: 'installationProjects_column_expectedCompletion',
            filter: 'date',
        },
    ] as const;

    protected readonly filterForm = this.formBuilder.group({
        projectNumber: '',
        buildingNumber: '',
        buildingName: '',
        statuses: new FormControl<SelectableItem[]>(
            FILTERABLE_STATUSES.map(status => ({
                name: this.translate(installationProjectStatusDisplay(status).labelKey),
                selected: false,
            })),
            {nonNullable: true},
        ),
        address: '',
        // Order matters: index 0 is the awaiting flag, index 1 the ok flag.
        customerInformation: new FormControl<SelectableItem[]>(
            [
                'installationProjects_customerInfo_awaiting',
                'installationProjects_customerInfo_ok',
            ].map(key => ({name: this.translate(key), selected: false})),
            {nonNullable: true},
        ),
        plannedStart: new FormControl<DateTime | null>(null),
        expectedCompletion: new FormControl<DateTime | null>(null),
    });

    private readonly filter = toSignal(
        combineLatest({
            projectNumber: fromControl(this.filterForm.controls.projectNumber),
            buildingNumber: fromControl(this.filterForm.controls.buildingNumber),
            buildingName: fromControl(this.filterForm.controls.buildingName),
            statuses: fromControl(this.filterForm.controls.statuses),
            address: fromControl(this.filterForm.controls.address),
            customerInformation: fromControl(this.filterForm.controls.customerInformation),
            plannedStart: fromControl(this.filterForm.controls.plannedStart),
            expectedCompletion: fromControl(this.filterForm.controls.expectedCompletion),
        }).pipe(
            map(({statuses, customerInformation, ...values}) => ({
                ...values,
                selectedStatuses: FILTERABLE_STATUSES.filter(
                    (_, index) => statuses[index].selected,
                ),
                awaitingSelected: customerInformation[0].selected,
                okSelected: customerInformation[1].selected,
            })),
        ),
        {requireSync: true},
    );

    protected readonly sorting = signal<Sorting | null>(null);

    /** Fetched only while the list itself is on screen, so opening a detail tab from a link does
     * not read Dynamics for a list nobody sees, and coming back from a tab re-reads it. */
    protected readonly view = toSignal(
        toObservable(this.noActivatedChildren).pipe(
            switchMap(listVisible =>
                listVisible
                    ? this.installationProjectsService.getInstallationProjects().pipe(
                          map(
                              (projects): InstallationProjectsView => ({
                                  status: 'success',
                                  projects,
                              }),
                          ),
                          startWith<InstallationProjectsView>({status: 'loading'}),
                          catchError(() => of<InstallationProjectsView>({status: 'error'})),
                      )
                    : EMPTY,
            ),
            startWith<InstallationProjectsView>({status: 'loading'}),
        ),
        {requireSync: true},
    );

    private readonly allRows = computed<InstallationProjectRow[]>(() => {
        const view = this.view();
        if (view.status !== 'success') {
            return [];
        }
        return view.projects.map(project => ({
            project,
            address: formatStreetAddress({
                address1: project.street,
                address2: null,
                postalCode: project.postalCode,
                city: project.city,
            }),
            statusDisplay: installationProjectStatusDisplay(project.status),
            awaitingCustomerInfo: project.status === 'awaiting_customer_info',
        }));
    });

    private readonly filteredRows = computed<InstallationProjectRow[]>(() => {
        const filter = this.filter();
        return this.allRows().filter(
            row =>
                includesIgnoreCase(row.project.projectNumber, filter.projectNumber) &&
                includesIgnoreCase(row.project.buildingNumber, filter.buildingNumber) &&
                includesIgnoreCase(row.project.buildingName, filter.buildingName) &&
                includesIgnoreCase(row.address, filter.address) &&
                (filter.selectedStatuses.length === 0 ||
                    filter.selectedStatuses.includes(row.project.status)) &&
                matchesCustomerInfo(row.awaitingCustomerInfo, filter) &&
                matchesDay(row.project.plannedStart, filter.plannedStart) &&
                matchesDay(row.project.expectedCompletion, filter.expectedCompletion),
        );
    });

    /** Filtered rows in the selected sort order; unsorted keeps the backend order (newest first). */
    protected readonly rows = computed<InstallationProjectRow[]>(() => {
        const sorting = this.sorting();
        if (sorting === null) {
            return this.filteredRows();
        }
        return [...this.filteredRows()].sort(rowComparator(sorting));
    });

    /**
     * Narrowing the filter while paged in would otherwise leave the stored page behind, and
     * clearing the filter again would jump back to it.
     */
    private readonly page = linkedSignal({source: this.filter, computation: () => 1});
    protected readonly pageSize = signal(20);

    /** The requested page, clamped so shrinking the filter result never strands the view. */
    protected readonly currentPage = computed(() =>
        Math.min(this.page(), Math.max(1, Math.ceil(this.rows().length / this.pageSize()))),
    );

    protected readonly pagedRows = computed<InstallationProjectRow[]>(() => {
        const start = (this.currentPage() - 1) * this.pageSize();
        return this.rows().slice(start, start + this.pageSize());
    });

    /** The table is destroyed while a detail tab is open, so a fresh header has to read the
     * retained sort back or the rows stay sorted with no arrow and the next click is a no-op. */
    protected sortDirectionFor(column: string): SortDirection | null {
        const sorting = this.sorting();
        return sorting?.column === column ? sorting.direction : null;
    }

    protected onSort(sorting: Sorting): void {
        applySortingToHeaders(this.headers(), sorting);
        this.sorting.set(sorting);
        this.page.set(1);
    }

    protected setPage(page: number): void {
        this.page.set(page);
    }

    protected setPageSize(pageSize: number): void {
        this.pageSize.set(pageSize);
        this.page.set(1);
    }

    protected exportFilteredProjects(): void {
        const headers = [
            'installationProjects_column_projectId',
            'installationProjects_column_buildingId',
            'installationProjects_column_project',
            'installationProjects_column_status',
            'installationProjects_column_address',
            'installationProjects_column_customerInformation',
            'installationProjects_column_plannedStart',
            'installationProjects_column_expectedCompletion',
        ].map(key => this.translate(key));
        downloadCsv('installation-projects', [
            headers,
            ...this.rows().map(row => [
                row.project.projectNumber,
                row.project.buildingNumber,
                row.project.buildingName,
                this.translate(row.statusDisplay.labelKey),
                row.address,
                this.translate(
                    row.awaitingCustomerInfo
                        ? 'installationProjects_customerInfo_awaiting'
                        : 'installationProjects_customerInfo_ok',
                ),
                row.project.plannedStart?.toFormat('dd.MM.yyyy') ?? null,
                row.project.expectedCompletion?.toFormat('dd.MM.yyyy') ?? null,
            ]),
        ]);
    }

    private translate(key: string): string {
        return this.translateService.translate(key, 'online', 'ngonline');
    }
}

function rowComparator(
    sorting: Sorting,
): (a: InstallationProjectRow, b: InstallationProjectRow) => number {
    const order = sorting.direction;
    switch (sorting.column) {
        case 'buildingNumber':
            return numericLocaleComparator(row => row.project.buildingNumber ?? '', order);
        case 'buildingName':
            return caseInsensitiveComparator(row => row.project.buildingName ?? '', order);
        case 'status':
            return keyComparator(row => statusOrder(row.project.status), order);
        case 'address':
            return caseInsensitiveComparator(row => row.address, order);
        case 'customerInformation':
            return keyComparator(row => (row.awaitingCustomerInfo ? 0 : 1), order);
        case 'plannedStart':
            return keyComparator(row => row.project.plannedStart?.toMillis() ?? null, order);
        case 'expectedCompletion':
            return keyComparator(row => row.project.expectedCompletion?.toMillis() ?? null, order);
        default:
            return numericLocaleComparator(row => row.project.projectNumber ?? '', order);
    }
}

/** Position in the 7-phase lifecycle; `unknown` sorts after every known phase. */
function statusOrder(status: InstallationProjectStatus): number {
    const index = FILTERABLE_STATUSES.indexOf(status);
    return index === -1 ? FILTERABLE_STATUSES.length : index;
}

/**
 * Selecting neither flag - or both - matches everything, since the column only has these two
 * values; selecting exactly one narrows to the rows carrying it.
 */
function matchesCustomerInfo(
    awaitingCustomerInfo: boolean,
    filter: {awaitingSelected: boolean; okSelected: boolean},
): boolean {
    if (filter.awaitingSelected === filter.okSelected) {
        return true;
    }
    return awaitingCustomerInfo === filter.awaitingSelected;
}

/** No filter date matches everything; a filter date matches that exact calendar day. */
function matchesDay(date: DateTime | null, filterValue: DateTime | null): boolean {
    if (filterValue === null) {
        return true;
    }
    return date !== null && date.hasSame(filterValue, 'day');
}
